import { Flex, List, Stack, Text, Box, Loader } from '@mantine/core';
import {
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation
} from '#/entities/notification-setting/api';
import {
  NotificationSetting,
  NotificationSettingItem,
  NotificationSettingValue,
  NotificationType
} from '#/entities/notification-setting';
import { useGroupedSettings } from './useGroupedSettings';
import { NotificationSettingValueDictionary } from '#/shared/dictionaries';
import { IconBell, IconBellOff, IconStar } from '@tabler/icons';
import { useCurrentUser } from '#/shared/hooks/useCurrentUser';
import { Module } from '#/shared/types';

export const NotificationSettings: React.FC = () => {
  const { userId } = useCurrentUser();
  const { data: settings, isLoading } = useGetNotificationSettingsQuery(
    { userId: userId as number },
    { skip: !userId }
  );
  const [updateSetting] = useUpdateNotificationSettingsMutation();
  const groupedSettings = useGroupedSettings(settings);

  const renderLabel = (item: ReturnType<NotificationSettingValueDictionary['get']>) => (
    <Flex gap="xs" align="center">
      {item.id === NotificationSettingValue.Off && <IconBellOff size={16} />}
      {item.id === NotificationSettingValue.Self && <IconStar size={16} />}
      {item.id === NotificationSettingValue.All && <IconBell size={16} />}
      <Text size="sm">{item.title}</Text>
    </Flex>
  );
  const handleSettingsChange = async (
    value: NotificationSettingValue,
    item: NotificationSettingItem,
    source: Module
  ) => {
    if (value === item.value || !userId) {
      return;
    }

    const existingSettings = new Map<NotificationType, NotificationSetting>(
      settings?.map((setting) => [setting.id, setting]) ?? []
    );

    const updatedSettings = item.notificationTypes.reduce((acc, type) => {
      acc.set(type, {
        id: type,
        notificationType: type,
        source,
        value
      });
      return acc;
    }, existingSettings);

    await updateSetting({
      userId,
      settings: Array.from(updatedSettings.values())
    });
  };

  if (isLoading) {
    return (
      <Flex justify="center">
        <Loader />
      </Flex>
    );
  }

  return (
    <Box>
      <Stack gap="xl">
        {groupedSettings?.map((source) => (
          <List key={source.id} title={source.title}>
            <Text size="xl" fw={500} mb="xs" c="primary">
              {source.title}
            </Text>
            <Stack gap="sm">
              {source.items?.map((item) => (
                <div key={item.id}>
                  <NotificationSettingItem
                    key={item.id}
                    item={item}
                    source={source.id}
                    renderLabel={renderLabel}
                    onChange={handleSettingsChange}
                  />
                </div>
              ))}
            </Stack>
          </List>
        ))}
      </Stack>
    </Box>
  );
};
